import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatCurrencyMajorUnit } from '../../util/currency';
import config from '../../config';

import css from './SearchFiltersModalV2.module.css';

const DEFAULT_DISTRIBUTION = [];

const PriceRangeSection = props => {
  const { priceDefaults, currentPrice, priceDistribution, onPriceChange, intl, label } = props;

  const distribution = Array.isArray(priceDistribution) ? priceDistribution : DEFAULT_DISTRIBUTION;
  const priceSpan = priceDefaults.maxPrice - priceDefaults.minPrice || 1;
  const minPercent = ((currentPrice.minPrice - priceDefaults.minPrice) / priceSpan) * 100;
  const maxPercent = ((currentPrice.maxPrice - priceDefaults.minPrice) / priceSpan) * 100;
  const formattedMinPrice = formatCurrencyMajorUnit(
    intl,
    config.currencyConfig.currency,
    currentPrice.minPrice
  );
  const formattedMaxPrice = formatCurrencyMajorUnit(
    intl,
    config.currencyConfig.currency,
    currentPrice.maxPrice
  );
  const minLabel = intl.formatMessage({ id: 'SearchFiltersModalV2.min' });
  const maxLabel = intl.formatMessage({ id: 'SearchFiltersModalV2.max' });

  return (
    <section className={css.section}>
      <h3 className={css.sectionTitle}>{label}</h3>
      <p className={css.sectionSubtitle}>
        <FormattedMessage id="SearchFiltersModalV2.priceHelp" />
      </p>
      <div className={css.priceChart}>
        {distribution.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className={css.priceBar}
            style={{ height: `${value}px` }}
          />
        ))}
      </div>
      <div className={css.rangeSlider}>
        <div className={css.rangeTrack} />
        <div
          className={css.rangeTrackActive}
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
          }}
        />
        <span
          className={`${css.rangeTooltip} ${css.rangeTooltipMin}`}
          style={{ left: `${minPercent}%` }}
        >
          {formattedMinPrice}
        </span>
        <span
          className={`${css.rangeTooltip} ${css.rangeTooltipMax}`}
          style={{ left: `${maxPercent}%` }}
        >
          {formattedMaxPrice}
        </span>
        <input
          className={css.rangeInput}
          type="range"
          min={priceDefaults.minPrice}
          max={priceDefaults.maxPrice}
          step={priceDefaults.step}
          value={currentPrice.minPrice}
          aria-label={minLabel}
          title={formattedMinPrice}
          onChange={event => onPriceChange(Number(event.target.value), currentPrice.maxPrice)}
        />
        <input
          className={css.rangeInput}
          type="range"
          min={priceDefaults.minPrice}
          max={priceDefaults.maxPrice}
          step={priceDefaults.step}
          value={currentPrice.maxPrice}
          aria-label={maxLabel}
          title={formattedMaxPrice}
          onChange={event => onPriceChange(currentPrice.minPrice, Number(event.target.value))}
        />
      </div>
      <div className={css.priceInputs}>
        <div className={css.pricePill}>
          <span className={css.pricePillLabel}>
            <FormattedMessage id="SearchFiltersModalV2.min" />
          </span>
          <span className={css.pricePillValue}>{formattedMinPrice}</span>
        </div>
        <div className={css.pricePill}>
          <span className={css.pricePillLabel}>
            <FormattedMessage id="SearchFiltersModalV2.max" />
          </span>
          <span className={css.pricePillValue}>{formattedMaxPrice}</span>
        </div>
      </div>
    </section>
  );
};

PriceRangeSection.defaultProps = {
  priceDistribution: DEFAULT_DISTRIBUTION,
  label: null,
};

PriceRangeSection.propTypes = {
  priceDefaults: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
  }).isRequired,
  currentPrice: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
  }).isRequired,
  priceDistribution: PropTypes.arrayOf(PropTypes.number),
  onPriceChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  label: PropTypes.node,
};

export default PriceRangeSection;
